// Course-zone time helpers — the frontend's single point of timezone
// conversion. Storage, devices, and every API parameter stay UTC;
// the course zone exists only between these functions and the user's eyes.
//
// Every function here is pure: the zone is an argument, never module state.
// React components take a bound copy from useCourseTime(), which reads the
// zone from DashboardContext, so a component re-renders when the zone arrives
// with the dashboard_status poll. A module-level variable could not do that —
// React cannot observe a plain assignment, so any component that had already
// rendered kept the pre-poll zone for the rest of its mount.
//
// Same 10-year IANA slice as deployment.js: ample for display, light in the bundle.
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range';

const DB_FORMAT = 'YYYY-MM-DD HH:mm:ss';

// Validates a zone name from the API, falling back to the browser's own zone.
// The browser is a closer guess than UTC: every user today sits in the course's
// zone, so the fallback renders correct local times before the first poll lands
// and for any tenant whose TIMEZONE property has not been seeded yet.
export function resolveZone(name) {
  return typeof name === 'string' && moment.tz.zone(name) ? name : moment.tz.guess();
}

// UTC instant (DB string, ISO string, or moment) -> course-zone moment.
export function toLocal(utcInput, zone) {
  return moment.utc(utcInput).tz(zone);
}

// UTC instant -> formatted course-zone text. The display workhorse.
export function display(utcInput, format, zone) {
  return toLocal(utcInput, zone).format(format);
}

// "Now" as a course-zone moment. Pickers and default ranges must start from
// this rather than moment(), so their wall-clock digits are course-local and
// toUtcParam reads them back correctly from any browser zone.
export function nowLocal(zone) {
  return moment.tz(zone);
}

// Picker moment -> UTC DB param. Only the picker's wall-clock DIGITS matter:
// they are reinterpreted in the course zone, so the window the user saw is the
// window the query means, regardless of the browser's zone.
export function toUtcParam(pickerMoment, zone) {
  return moment.tz(pickerMoment.format(DB_FORMAT), DB_FORMAT, zone).utc().format(DB_FORMAT);
}

// Course-zone time-of-day -> UTC time-of-day, anchored on a course-zone date
// (default: today). The anchor only matters on DST-transition days.
export function localTimeOfDayToUtc(hhmmss, anchorLocalDate, zone) {
  const date = anchorLocalDate || moment.tz(zone).format('YYYY-MM-DD');
  return moment.tz(`${date} ${hhmmss}`, DB_FORMAT, zone).utc().format('HH:mm:ss');
}

// The helpers with `zone` already applied. useCourseTime() returns this for
// components; non-component callers (StationInfoDisplay, which is a factory
// invoked as a plain function, and tests) build one directly so there is a
// single bound shape rather than two ways to carry a zone around.
export function bind(zone) {
  return {
    zone,
    toLocal: (utcInput) => toLocal(utcInput, zone),
    display: (utcInput, format) => display(utcInput, format, zone),
    nowLocal: () => nowLocal(zone),
    toUtcParam: (pickerMoment) => toUtcParam(pickerMoment, zone),
    localTimeOfDayToUtc: (hhmmss, anchorLocalDate) =>
      localTimeOfDayToUtc(hhmmss, anchorLocalDate, zone),
  };
}
